import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditApplicationCategory, CreditApplicationSortField, SortDirection } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton from '@divvy-web/skylab.basicbutton'
import { DataGrid, FooterToolbar, LoadMore, Table, ZeroState } from '@divvy-web/skylab.datagridnext'
import {
  ColumnFiltersState,
  getCoreRowModel,
  SortingState,
  TableState,
  useReactTable,
} from '@divvy-web/skylab.tanstack-react-table'

import { PATHNAME_CONSTS } from '@sputnik/resources/constants'

import { useCheckActionNeededCreditApplicationsCountSuspenseQuery } from './CheckActionNeededCreditApplicationsCount.gql'
import { getColumnConfiguration } from './columns'
import DashboardDetailsDrawer from './DashboardDetailsDrawer'
import DataGridHeader from './DataGridHeader'
import {
  GetCreditApplications_creditApplicationsPaginated_CreditApplicationPaginatedConnection_edges_CreditApplicationPaginatedEdge,
  GetCreditApplicationsVariables,
  useGetCreditApplications,
} from './GetCreditApplications.gql'
import styles from './styles.module.scss'

type CreditApplication =
  GetCreditApplications_creditApplicationsPaginated_CreditApplicationPaginatedConnection_edges_CreditApplicationPaginatedEdge

const getSortField = (value: string) => {
  switch (value) {
    case 'business-name':
      return CreditApplicationSortField.LEGAL_BUSINESS_NAME
    case 'status':
      return CreditApplicationSortField.STATUS_WEIGHT
    case 'line-of-credit':
      return CreditApplicationSortField.FUNDABLE_LIMIT
    case 'expires-at':
      return CreditApplicationSortField.EXPIRES_AT
    case 'started-at':
    default:
      return CreditApplicationSortField.STARTED_AT
  }
}

const getColumnFilterCreditApplicationCategory = (value: unknown) => {
  switch (value) {
    case CreditApplicationCategory.ACTION_NEEDED:
      return CreditApplicationCategory.ACTION_NEEDED
    case CreditApplicationCategory.COMPLETED:
      return CreditApplicationCategory.COMPLETED
    case CreditApplicationCategory.IN_REVIEW:
      return CreditApplicationCategory.IN_REVIEW
    case CreditApplicationCategory.ALL:
    default:
      return CreditApplicationCategory.ALL
  }
}

const getCategoryFromTableState = (columnFilters?: ColumnFiltersState) => {
  if (!columnFilters?.[0]) {
    return null
  }
  const [{ value }] = columnFilters
  return getColumnFilterCreditApplicationCategory(value)
}

const getSortingParamsFromTableState = (sortingState?: SortingState) => {
  if (!sortingState) {
    return { orderBy: [], orderDirections: [] }
  }
  const orderBy: CreditApplicationSortField[] = []
  const orderDirections: SortDirection[] = []
  sortingState.forEach(({ id, desc }) => {
    orderBy.push(getSortField(id))
    orderDirections.push(desc ? SortDirection.DESC : SortDirection.ASC)
  })

  // we need to pass ID as a tie breaking sort param
  if (orderBy.length > 0) {
    orderBy.push(CreditApplicationSortField.ID)
    orderDirections.push(orderDirections[orderDirections.length - 1])
  }

  return { orderBy, orderDirections }
}

const pageSize = 20

const getCreditApplicationQueryParams = (
  tableState: Partial<TableState>,
  overrides?: Partial<GetCreditApplicationsVariables>,
): Partial<GetCreditApplicationsVariables> => {
  return {
    first: pageSize,
    legalBusinessName: tableState.globalFilter,
    category: getCategoryFromTableState(tableState.columnFilters),
    ...getSortingParamsFromTableState(tableState.sorting),
    ...overrides,
  }
}

const CreditApplicationsDataGrid = () => {
  // This suspense query is so we know to render "action needed" applications or "all" on initial load
  const { data: suspenseData } = useCheckActionNeededCreditApplicationsCountSuspenseQuery({
    fetchPolicy: 'no-cache',
  })

  const hasActionNeededApplications = (suspenseData?.creditApplicationsPaginated?.totalCount ?? 0) > 0

  const [getClassName] = useNameFormatter('CreditApplicationsDataGrid')
  const navigate = useNavigate()

  const onStartNewApplication = () => {
    navigate(PATHNAME_CONSTS.GET_TO_KNOW_YOU)
  }

  const [tableState, setTableState] = useState<Partial<TableState>>(() => ({
    globalFilter: '',
    columnFilters: hasActionNeededApplications
      ? [{ id: 'status', value: CreditApplicationCategory.ACTION_NEEDED }]
      : [],
    sorting: [],
  }))

  const { data, loading, fetchMore, refetch } = useGetCreditApplications({
    variables: {
      ...getCreditApplicationQueryParams(tableState),
    },
    fetchPolicy: 'network-only',
  })

  const { edges, pageInfo, totalCount } = data?.creditApplicationsPaginated ?? {}
  const { hasNextPage, endCursor } = pageInfo ?? {}
  const [rowClickedApplicationDetails, setRowClickedApplicationDetails] = useState<string | null>(null)

  const columns = getColumnConfiguration({
    onViewDetailsClick: (newSalesforceCreditId: string) => setRowClickedApplicationDetails(newSalesforceCreditId),
  })

  const applications = useMemo(() => edges?.filter((edge): edge is CreditApplication => edge !== null) ?? [], [edges])

  const table = useReactTable({
    data: applications,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualSorting: true,
    state: tableState,
    onGlobalFilterChange: (value) => {
      // calling refetch resets the pagination / search
      setTableState((prev) => ({
        ...prev,
        globalFilter: value,
      }))
      refetch({
        ...getCreditApplicationQueryParams(tableState),
        legalBusinessName: value,
      })
    },
    onSortingChange: (updater) => {
      const sortingState = typeof updater === 'function' ? updater(tableState.sorting ?? []) : updater

      const { orderBy, orderDirections } = getSortingParamsFromTableState(sortingState)

      setTableState((prev) => ({
        ...prev,
        sorting: sortingState,
      }))

      refetch({
        ...getCreditApplicationQueryParams(tableState),
        orderBy,
        orderDirections,
      })
    },
    onColumnFiltersChange: (updater) => {
      const filters = typeof updater === 'function' ? updater(tableState.columnFilters ?? []) : updater
      setTableState((prev) => ({
        ...prev,
        columnFilters: filters,
      }))

      refetch({
        ...getCreditApplicationQueryParams(tableState),
        category: getCategoryFromTableState(filters),
      })
    },
  })

  const handleLoadMore = useCallback(() => {
    if (hasNextPage) {
      fetchMore({
        variables: {
          ...getCreditApplicationQueryParams(tableState),
          after: endCursor,
        },
      })
    }
  }, [endCursor, fetchMore, hasNextPage, tableState])

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <div className={styles[getClassName('title')]}>
        <FormattedMessage
          defaultMessage='Manage your applications'
          id='sputnik.CreditApplicationsDataGrid__Lecg3+'
        />
        <BasicButton onClick={onStartNewApplication}>
          <FormattedMessage
            defaultMessage='New application'
            id='sputnik.CreditApplicationsDataGrid__HOulGI'
          />
        </BasicButton>
      </div>
      <DataGrid.Root
        initialIsSearching
        initialIsShowingFilters
        hasMore={hasNextPage}
        isLoading={loading}
        table={table}
        onLoadMore={handleLoadMore}
      >
        <DataGridHeader />
        <DataGrid.Body>
          <Table<CreditApplication>
            enableInfiniteScroll
            onRowClick={(row) => {
              if (row?.original?.node?.salesforceCreditId) {
                setRowClickedApplicationDetails(row.original.node.salesforceCreditId)
              }
            }}
          />
          <DataGrid.ZeroStateInitial>
            <ZeroState.Root>
              <ZeroState.Title>
                <FormattedMessage
                  defaultMessage='You have no credit applications'
                  id='sputnik.CreditApplicationsDataGrid__GGpz3M'
                />
              </ZeroState.Title>
              <ZeroState.Content>
                <BasicButton onClick={onStartNewApplication}>
                  <FormattedMessage
                    defaultMessage='Start new application'
                    id='sputnik.CreditApplicationsDataGrid__Whk28C'
                  />
                </BasicButton>
              </ZeroState.Content>
            </ZeroState.Root>
          </DataGrid.ZeroStateInitial>
          <DataGrid.ZeroStateNoResults>
            <ZeroState.Root>
              <ZeroState.Title>
                <FormattedMessage
                  defaultMessage='No credit applications found matching your search'
                  id='sputnik.CreditApplicationsDataGrid__+FVNHE'
                />
              </ZeroState.Title>
              <ZeroState.Description>
                <FormattedMessage
                  defaultMessage='Try adjusting your search. Make sure the term you are searching for matches the legal business name on the application.'
                  id='sputnik.CreditApplicationsDataGrid__c4tKNB'
                />
              </ZeroState.Description>
            </ZeroState.Root>
          </DataGrid.ZeroStateNoResults>
        </DataGrid.Body>
        <DataGrid.Footer>
          <FooterToolbar.Root>
            {({ rowCountEnd }) => {
              return (
                <>
                  <FooterToolbar.ItemIndicator>
                    <FormattedMessage
                      defaultMessage='<pageSizeSelector>{rowCountEnd}</pageSizeSelector> of {rowCount, plural, one {{rowCount} Application} other {{rowCount} Applications}}'
                      id='sputnik.CreditApplicationsDataGrid__8NsOdP'
                      values={{
                        pageSizeSelector: (chunk) => (
                          <FooterToolbar.PageSizeSelector>{chunk}</FooterToolbar.PageSizeSelector>
                        ),
                        rowCountEnd,
                        rowCount: totalCount,
                      }}
                    />
                  </FooterToolbar.ItemIndicator>
                  <DataGrid.FlexSpacer />
                  <LoadMore
                    buttonLabel={
                      <FormattedMessage
                        defaultMessage='Load More'
                        id='sputnik.CreditApplicationsDataGrid__xMnH8F'
                      />
                    }
                    loadingLabel={
                      <FormattedMessage
                        defaultMessage='Loading...'
                        id='sputnik.CreditApplicationsDataGrid__gjBiyj'
                      />
                    }
                    noMoreLabel={
                      <FormattedMessage
                        defaultMessage='All applications loaded'
                        id='sputnik.CreditApplicationsDataGrid__ND3S0i'
                      />
                    }
                  />
                  <FooterToolbar.ScrollControls />
                </>
              )
            }}
          </FooterToolbar.Root>
        </DataGrid.Footer>
      </DataGrid.Root>
      <DashboardDetailsDrawer
        isShowing={rowClickedApplicationDetails !== null}
        salesforceCreditId={rowClickedApplicationDetails ?? ''}
        onClose={() => setRowClickedApplicationDetails(null)}
      />
    </div>
  )
}

export default CreditApplicationsDataGrid
